import type {
  NotifyMessageOptions,
  NotifyMessageCardFields,
  NotificationColor,
} from '@/shared/types/message';

/**
 * Color de cola interno (timers / capas) según clave de mazo.
 *
 * @param stackKey - `'secondary'` mapea a cola neutral; resto a cola brand.
 * @returns Token de color usado por el servicio.
 */
export function stackKeyToQueueColor(stackKey: string): NotificationColor {
  if (stackKey === 'secondary') return 'neutral';
  return 'brand';
}

/**
 * Une campos de tarjeta en raíz de `notify.message` con `notification` (por bloque; en cada bloque gana `notification`).
 *
 * @param options - Payload completo `NotifyMessageOptions` (raíz + bloque anidado opcional).
 * @returns Objeto de overrides para `Notification` o `undefined` si no hay nada que fusionar.
 */
export function mergeNotificationCardFromNotifyMessageOptions(
  options: NotifyMessageOptions
): NotifyMessageCardFields | undefined {
  const nested = options.notification;
  const rc = options.content;
  const rl = options.leading;
  const ra = options.appearance;
  const rm = options.meta;
  const re = options.events;
  const rf = options.footer;

  const hasRoot =
    rc !== undefined ||
    rl !== undefined ||
    ra !== undefined ||
    rm !== undefined ||
    re !== undefined ||
    rf !== undefined;

  if (!nested && !hasRoot) {
    return undefined;
  }

  const out: NotifyMessageCardFields = {};

  if (rc !== undefined || nested?.content !== undefined) {
    const baseContent = { ...rc, ...nested?.content };
    const rootCta = rc?.cta;
    const nestedCta = nested?.content?.cta;
    if (rootCta !== undefined || nestedCta !== undefined) {
      baseContent.cta = {
        ...rootCta,
        ...nestedCta,
        primaryButton: nestedCta?.primaryButton ?? rootCta?.primaryButton,
        secondaryButton: nestedCta?.secondaryButton ?? rootCta?.secondaryButton,
      };
    }
    out.content = baseContent as NotifyMessageCardFields['content'];
  }
  if (rl !== undefined || nested?.leading !== undefined) {
    out.leading = { ...rl, ...nested?.leading };
  }
  if (ra !== undefined || nested?.appearance !== undefined) {
    out.appearance = { ...ra, ...nested?.appearance };
  }
  if (rm !== undefined || nested?.meta !== undefined) {
    out.meta = { ...rm, ...nested?.meta };
  }
  if (re !== undefined || nested?.events !== undefined) {
    const baseEvents = { ...re, ...nested?.events };
    const rootPb = re?.primaryButton;
    const nestedPb = nested?.events?.primaryButton;
    if (rootPb !== undefined || nestedPb !== undefined) {
      baseEvents.primaryButton = nestedPb ?? rootPb;
    }
    const rootSb = re?.secondaryButton;
    const nestedSb = nested?.events?.secondaryButton;
    if (rootSb !== undefined || nestedSb !== undefined) {
      baseEvents.secondaryButton = nestedSb ?? rootSb;
    }
    out.events = baseEvents;
  }
  if (rf !== undefined) {
    out.footer = rf;
  } else if (nested?.footer !== undefined) {
    out.footer = nested.footer;
  }

  return Object.keys(out).length > 0 ? out : undefined;
}
